/**
 * # FilterChip
 *
 * A custom filter chip component built with React and React Select.
 *
 * ## Props
 *
 * - `value` (object, required): The selected value of the filter chip.
 * - `onChange` (function, required): The callback function called when the value of the filter chip changes.
 * - `label` (string): The label of the filter chip.
 * - `color` (string): The color of the filter chip. Must be one of type ChipColor
 * - `options` (array): The options for the filter chip.
 * - `disabled` (boolean): Specifies whether the filter chip is disabled.
 * - `hasFocus` (boolean): Specifies whether the filter chip has focus.
 * - `hasHover` (boolean): Specifies whether the filter chip is being hovered.
 * - `role` (string): The role of the filter chip.
 * - `className` (string): The CSS class name for the filter chip.
 *
 * ## Usage
 *
 * ```jsx
 * import FilterChip from './FilterChip.react';
 *
 * const options = [
 *   { value: 'option1', label: 'Option 1' },
 *   { value: 'option2', label: 'Option 2' },
 *   { value: 'option3', label: 'Option 3' },
 * ];
 *
 * const MyComponent = () => {
 *   const [selectedValue, setSelectedValue] = useState(null);
 *
 *   const handleChange = (value) => {
 *     setSelectedValue(value);
 *   };
 *
 *   return (
 *     <FilterChip
 *       value={selectedValue}
 *       onChange={handleChange}
 *       label="Filter"
 *       color="brand"
 *       options={options}
 *       disabled={false}
 *       hasFocus={false}
 *       hasHover={false}
 *       role="button"
 *       className="my-filter-chip"
 *     />
 *   );
 * };
 * ```
 */
import React from 'react';
import classnames from 'classnames';
import Select, {components} from 'react-select';
import type {ControlProps, SingleValue, StylesConfig, ValueContainerProps} from 'react-select';

import Chip from '@albert-io/atomic/atoms/Chip/Chip.react';
import type {ChipColor} from '@albert-io/atomic/atoms/Chip/Chip.react';
import Text from '@albert-io/atomic/atoms/Text/Text.react';
import WithHoverAndFocus from '@albert-io/atomic/helpers/WithHoverAndFocus/WithHoverAndFocus.react';

import './filter-chip.scss';

interface FilterChipOption {
  value: string;
  label: string;
}

const {ValueContainer} = components;

const customStyles: StylesConfig<FilterChipOption, false> = {
  control: () => ({
    display: 'flex',
    cursor: 'pointer',
    width: '100%'
  }),
  dropdownIndicator: () => ({
    color: 'inherit',
    display: 'flex'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  option: (provided, state) => ({
    ...provided,
    borderBottom: '1px solid #dfe0e0',
    color: state.isSelected ? '#399fdf' : 'rgba(0, 0, 0, 0.7)', // selected: $blue-400
    cursor: 'pointer',
    fontWeight: 700,
    padding: 16,
    background: 'transparent'
  }),
  menu: (provided) => ({
    ...provided,
    left: 0,
    width: 'auto',
    minWidth: '100%'
  }),
  valueContainer: () => ({
    flexWrap: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flex: 1
  }),
  menuPortal: (base) => ({...base, zIndex: 9999})
};

/**
 * `selectProps` carries FilterChip's custom props (`label`, `color`) alongside
 * react-select's own — react-select has no types for those extras, so we widen it here.
 */
type FilterChipSelectProps = ControlProps<FilterChipOption, false>['selectProps'] & {
  label?: string;
  color?: ChipColor;
};

const CustomControl = ({
  children,
  innerRef,
  innerProps,
  selectProps,
  ...props
}: ControlProps<FilterChipOption, false>) => {
  const {color} = selectProps as FilterChipSelectProps;
  return (
    <WithHoverAndFocus>
      {({hasFocus, hasHover, ...withHoverAndFocusHandlers}) => (
        <Chip
          ref={innerRef as React.Ref<HTMLSpanElement>}
          color={color ?? 'neutral'}
          hasFocus={hasFocus}
          hasHover={hasHover}
          style={{cursor: 'pointer'}}
          {...(innerProps as Record<string, unknown>)}
          {...withHoverAndFocusHandlers}
          {...(selectProps as unknown as Record<string, unknown>)}
          {...(props as unknown as Record<string, unknown>)}
        >
          {children}
        </Chip>
      )}
    </WithHoverAndFocus>
  );
};

const CustomValueContainer = ({
  children,
  ...props
}: ValueContainerProps<FilterChipOption, false>) => {
  const {label} = props.selectProps as FilterChipSelectProps;
  const value = props.selectProps.value as SingleValue<FilterChipOption>;
  const selectInput = React.Children.toArray(children).find((input) => {
    const inputType = (input as React.ReactElement<any>).type;
    const typeName = typeof inputType === 'function' ? inputType.name : undefined;
    return typeName === 'Input' || typeName === 'DummyInput';
  });
  return (
    <ValueContainer {...props}>
      <Text bold size='s' color='inherit' className='mr-x1'>
        {label}:
      </Text>
      <Text size='s' color='inherit' className='overflow-hidden text-ellipsis'>
        {value?.value || 'Any'}
      </Text>
      {selectInput}
    </ValueContainer>
  );
};

interface FilterChipProps {
  value: FilterChipOption;
  onChange: (value: FilterChipOption) => void;
  label?: string;
  color?: ChipColor;
  options?: FilterChipOption[];
  disabled?: boolean;
  hasFocus?: boolean;
  hasHover?: boolean;
  role?: string;
  className?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

const FilterChip = ({
  value,
  onChange,
  label = 'filterLabel',
  color,
  options = [],
  disabled,
  className,
  ...rest
}: FilterChipProps) => {
  // `label`, `color` and `disabled` aren't react-select props — they ride along in
  // `selectProps` so the custom Control/ValueContainer can read them. react-select v5
  // type-checks explicit JSX attributes, so pass them via spread to reach `selectProps`.
  const passthroughProps = {label, color, disabled};
  return (
    <Select
      className={classnames('m-filterchip__select', className)}
      styles={customStyles}
      aria-label={label}
      isSearchable={false}
      onChange={(newValue) => {
        // react-select passes `null` on clear; FilterChip has no clear affordance,
        // but guard anyway so consumers keep receiving a concrete option.
        if (newValue) {
          onChange(newValue);
        }
      }}
      value={value}
      data-testid='m-filterchip__select'
      options={options}
      components={{
        ValueContainer: CustomValueContainer,
        Control: CustomControl
      }}
      {...passthroughProps}
      {...rest}
    />
  );
};

export default FilterChip;
